import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const CART_LOCAL_KEY = 'cart';

type CartItem = {
    productId: string;
    quantity: number;
    variantId?: string;
};

const Cart: React.FC = () => {
    const { user } = useAuth();
    const [cart, setCart] = useState<CartItem[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [colorMap, setColorMap] = useState<{ [colorId: string]: string }>({});
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // 获取购物车
    useEffect(() => {
        const fetchCart = async () => {
            let items: CartItem[] = [];
            if (user) {
                // 1. 先从 localStorage 读取
                const cached = localStorage.getItem(`cart_${user.uid}`);
                const cacheTime = localStorage.getItem(`cart_${user.uid}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 1 * 60 * 1000; // 1分钟缓存

                if (cached && cacheAge < maxAge) {
                    try {
                        const cartData = JSON.parse(cached);
                        items = cartData.items || [];
                    } catch { }
                }

                // 2. 再请求 Firestore
                const cartDoc = await getDoc(doc(db, "carts", user.uid));
                if (cartDoc.exists()) {
                    items = cartDoc.data().items || [];

                    // 3. 缓存购物车数据
                    localStorage.setItem(`cart_${user.uid}`, JSON.stringify({ items }));
                    localStorage.setItem(`cart_${user.uid}_time`, Date.now().toString());
                }
            } else {
                const localCart = localStorage.getItem(CART_LOCAL_KEY);
                if (localCart) items = JSON.parse(localCart);
            }
            setCart(items);
        };
        fetchCart();
    }, [user]);

    // 只在cart的productId变化时fetch商品详情
    useEffect(() => {
        let ignore = false;
        const fetchProducts = async () => {
            // 只在首次加载时 loading
            if (products.length === 0) setLoading(true);
            const prods = [];
            const validCart: CartItem[] = [];

            for (const item of cart) {
                // 1. 先从 localStorage 读取产品数据
                const cached = localStorage.getItem(`product_${item.productId}`);
                const cacheTime = localStorage.getItem(`product_${item.productId}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 2 * 60 * 1000; // 2分钟缓存

                let data: any = null;

                if (cached && cacheAge < maxAge) {
                    try {
                        data = JSON.parse(cached);
                    } catch { }
                }

                // 2. 如果缓存不存在或过期，从Firestore获取
                if (!data) {
                    const prodDoc = await getDoc(doc(db, "products", item.productId));
                    if (prodDoc.exists()) {
                        data = prodDoc.data();

                        // 3. 缓存产品数据
                        localStorage.setItem(`product_${item.productId}`, JSON.stringify(data));
                        localStorage.setItem(`product_${item.productId}_time`, Date.now().toString());
                    }
                }

                if (data) {
                    prods.push({
                        ...data,
                        id: item.productId,
                        quantity: item.quantity,
                        variantId: item.variantId, // 保留 variantId
                    });
                    validCart.push(item);
                }
            }

            if (!ignore) {
                setProducts(prods);
                setLoading(false);
                // 如果有无效商品，自动清理购物车
                if (validCart.length !== cart.length) {
                    updateCart(validCart);
                }
            }
        };
        if (cart.length > 0) fetchProducts();
        else {
            setProducts([]);
            setLoading(false);
        }
        return () => { ignore = true; };
        // eslint-disable-next-line
    }, [JSON.stringify(cart.map(i => i.productId + (i.variantId || '')).sort())]);

    // 获取所有用到的 colorId
    useEffect(() => {
        const fetchColors = async () => {
            const colorIds = Array.from(
                new Set(
                    products
                        .flatMap(p => p.variants || [])
                        .map((v: any) => v.colorId)
                )
            );
            if (colorIds.length === 0) return;

            const map: { [colorId: string]: string } = {};

            // 1. 先从 localStorage 读取已缓存的颜色
            const uncachedColorIds: string[] = [];
            for (const colorId of colorIds) {
                const cached = localStorage.getItem(`color_${colorId}`);
                const cacheTime = localStorage.getItem(`color_${colorId}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 10 * 60 * 1000; // 10分钟缓存

                if (cached && cacheAge < maxAge) {
                    try {
                        const colorData = JSON.parse(cached);
                        map[colorId] = colorData.name;
                    } catch {
                        uncachedColorIds.push(colorId);
                    }
                } else {
                    uncachedColorIds.push(colorId);
                }
            }

            // 2. 只请求未缓存的颜色
            if (uncachedColorIds.length > 0) {
                const snapshot = await getDocs(collection(db, 'colors'));
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (uncachedColorIds.includes(doc.id) && data.name) {
                        map[doc.id] = data.name;

                        // 3. 缓存颜色数据
                        localStorage.setItem(`color_${doc.id}`, JSON.stringify(data));
                        localStorage.setItem(`color_${doc.id}_time`, Date.now().toString());
                    }
                });
            }

            setColorMap(map);
        };
        fetchColors();
    }, [products]);

    // 数量变化时只更新products里的quantity
    // 修改 updateProductQuantity，增加库存校验
    const updateProductQuantity = (productId: string, delta: number, variantId?: string) => {
        const prod = products.find(p => p.id === productId && p.variantId === variantId);
        if (prod) {
            // 校验库存
            let available = 0;
            if (prod.variants && variantId) {
                const variant = prod.variants.find((v: any) => v.colorId === variantId);
                available = variant ? variant.stock : 0;
            } else {
                available = Number(prod.availableNum) || 0;
            }
            const newQty = Math.max(1, prod.quantity + delta);
            if (newQty > available) {
                alert(`"${prod.title}" only has ${available} available in stock.`);
                return;
            }
        }
        setProducts(prev =>
            prev.map(prod =>
                prod.id === productId && prod.variantId === variantId
                    ? { ...prod, quantity: Math.max(1, prod.quantity + delta) }
                    : prod
            )
        );
        const newCart = cart.map(item =>
            item.productId === productId && item.variantId === variantId
                ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                : item
        );
        updateCart(newCart);
    };

    // 删除商品
    const removeItem = (productId: string, variantId?: string) => {
        setProducts(prev => prev.filter(prod => !(prod.id === productId && prod.variantId === variantId)));
        const newCart = cart.filter(item => !(item.productId === productId && item.variantId === variantId));
        updateCart(newCart);
    };

    // 更新购物车到本地或云端
    const updateCart = async (newCart: CartItem[]) => {
        setCart(newCart);
        if (user) {
            await setDoc(doc(db, "carts", user.uid), { items: newCart });

            // 同步更新缓存
            localStorage.setItem(`cart_${user.uid}`, JSON.stringify({ items: newCart }));
            localStorage.setItem(`cart_${user.uid}_time`, Date.now().toString());
        } else {
            localStorage.setItem(CART_LOCAL_KEY, JSON.stringify(newCart));
        }
        window.dispatchEvent(new Event('cart-updated'));
    };

    // 清空购物车
    const clearCart = async () => {
        setCart([]);
        setProducts([]);
        if (user) {
            await setDoc(doc(db, "carts", user.uid), { items: [] });

            // 同步更新缓存
            localStorage.setItem(`cart_${user.uid}`, JSON.stringify({ items: [] }));
            localStorage.setItem(`cart_${user.uid}_time`, Date.now().toString());
        } else {
            localStorage.setItem(CART_LOCAL_KEY, JSON.stringify([]));
        }
        window.dispatchEvent(new Event('cart-updated'));
    };

    const total = products.reduce((sum, p) => sum + p.price * p.quantity, 0);

    if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
    if (products.length === 0)
        return (
            <div style={{ padding: 40, minHeight: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <h2 style={{ color: '#888', fontWeight: 500 }}>Your cart is empty.</h2>
                <button onClick={() => navigate('/')} style={{ marginTop: 24, background: '#27ae60', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 18, cursor: 'pointer' }}>Go Shopping</button>
            </div>
        );

    return (
        <div style={{
            maxWidth: 900,
            margin: '0 auto',
            padding: 40,
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
            marginTop: 100,
            marginBottom: 100
        }}>
            <h1 style={{ fontWeight: 700, marginBottom: 32, fontSize: 28 }}>Shopping Cart</h1>
            <div>
                {products
                    .filter(prod =>
                        cart.some(
                            item =>
                                item.productId === prod.id &&
                                (item.variantId || '') === (prod.variantId || '')
                        )
                    )
                    .map(prod => {
                        // 获取当前变体
                        const variant = prod.variantId
                            ? prod.variants?.find((v: any) => v.colorId === prod.variantId)
                            : null;
                        const img = (variant && variant.image) ? variant.image : (prod.images?.[0] || 'https://via.placeholder.com/80');
                        return (
                            <div
                                key={prod.id + (prod.variantId || '')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: 24,
                                    padding: 18,
                                    borderRadius: 12,
                                    background: '#f8f9fa',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
                                }}
                            >
                                <img
                                    src={img}
                                    alt={prod.title}
                                    style={{ width: 80, height: 80, borderRadius: 8, marginRight: 18, objectFit: 'cover' }}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 6 }}>{prod.title}</div>
                                    {variant && (
                                        <div style={{ fontSize: 14, color: '#888', marginBottom: 4 }}>
                                            Color: {colorMap[variant.colorId] || ''}
                                            {/* color 色号显示 */}
                                            <span style={{
                                                display: 'inline-block',
                                                width: 16, height: 16, borderRadius: '50%',
                                                background: variant.colorCode, border: '1px solid #ccc', verticalAlign: 'middle', marginLeft: 4
                                            }} />
                                        </div>
                                    )}
                                    <div style={{ color: '#27ae60', fontWeight: 700, fontSize: 16 }}>${prod.price}/month</div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                                        <button
                                            onClick={() => updateProductQuantity(prod.id, -1, prod.variantId)}
                                            style={{
                                                width: 28, height: 28, borderRadius: '50%', border: 'none',
                                                background: '#e0e0e0', color: '#222', fontWeight: 700, fontSize: 18, cursor: 'pointer'
                                            }}
                                            disabled={prod.quantity <= 1}
                                        >-</button>
                                        <span style={{ margin: '0 16px', fontWeight: 500 }}>{prod.quantity}</span>
                                        <button
                                            onClick={() => updateProductQuantity(prod.id, 1, prod.variantId)}
                                            style={{
                                                width: 28, height: 28, borderRadius: '50%', border: 'none',
                                                background: '#27ae60', color: '#fff', fontWeight: 700, fontSize: 18, cursor: 'pointer'
                                            }}
                                        >+</button>
                                        <button
                                            onClick={() => removeItem(prod.id, prod.variantId)}
                                            style={{
                                                marginLeft: 24,
                                                background: 'none',
                                                border: 'none',
                                                color: '#e74c3c',
                                                fontWeight: 600,
                                                fontSize: 16,
                                                cursor: 'pointer'
                                            }}
                                            title="Remove"
                                        >Remove</button>
                                    </div>
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 18, marginLeft: 18 }}>
                                    ${(prod.price * prod.quantity).toFixed(2)}
                                </div>
                            </div>
                        );
                    })}
            </div>
            <div style={{
                fontWeight: 700,
                fontSize: 22,
                marginTop: 32,
                textAlign: 'right'
            }}>
                Total: ${total.toFixed(2)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 32 }}>
                <button
                    onClick={clearCart}
                    style={{
                        background: '#e74c3c',
                        color: '#fff',
                        padding: '14px 32px',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 700,
                        fontSize: 18,
                        cursor: 'pointer'
                    }}
                >
                    Clear Cart
                </button>
                <button
                    style={{
                        background: '#27ae60',
                        color: '#fff',
                        padding: '14px 40px',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 700,
                        fontSize: 20,
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        // 只传递可序列化字段
                        const safeProducts = products.map(({ id, title, price, quantity, images, variantId, variants }) => ({
                            id, title, price, quantity, images, variantId, variants
                        }));
                        navigate('/checkout', { state: { products: safeProducts } });
                    }}
                >
                    Checkout
                </button>
            </div>
        </div>
    );
};

export default Cart;